import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Button } from "@/components/ui/button.tsx";
import { z } from "zod";
import { TFunction } from "i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { getErrorResponse } from "@/api/common/handleAxiosError.ts";
import { useMemo, useState } from "react";
import {
  changeEmail,
  ChangeEmailErrorType,
} from "@/api/authentication/changeEmail.ts";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert.tsx";
import { AlertCircle, LoaderCircle } from "lucide-react";
import { updateUser, useUser } from "@/hooks/useUser.ts";
import { useTranslation } from "@/i18n";

const createFormSchema = (t: TFunction) =>
  z.object({
    email: z.string().email({ message: t("validation.invalidEmail") }),
    password: z.string().min(1, t("validation.passwordRequired")),
  });

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

const EditEmailDialog = ({ onClose }: { onClose: () => void }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const user = useUser();
  const { t } = useTranslation();
  const formSchema = useMemo(() => createFormSchema(t), [t]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: user!.email,
      password: "",
    },
  });

  const onSubmit = async (values: FormValues) => {
    setLoading(true);
    setError("");
    try {
      await changeEmail(values);
      await updateUser();
      onClose();
    } catch (e: unknown) {
      const response = getErrorResponse<ChangeEmailErrorType>(e);
      setError(response?.detail);
    }
    setLoading(false);
  };

  return (
    <DialogContent className="sm:max-w-[425px]">
      <DialogHeader>
        <DialogTitle>{t("editEmail.title")}</DialogTitle>
        <DialogDescription>{t("editEmail.description")}</DialogDescription>
      </DialogHeader>
      {error && (
        <Alert variant="destructive" className="mx-auto my-4 w-fit">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t("editEmail.failed")}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid gap-4 py-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.email")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("form.emailPlaceholder")}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        setError(undefined);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.password")}</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        setError(undefined);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <DialogFooter>
            <Button type="submit">
              {t("editEmail.submit")}{" "}
              {loading && <LoaderCircle className="animate-spin" />}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  );
};

export default EditEmailDialog;
